import React, { useState, useEffect } from 'react'
import { Link2, ExternalLink, AlertCircle, CheckCircle2 } from 'lucide-react'
import { apiClient } from '@/utils/api'

interface LinkedFamily {
  id: string
  name: string
  slug: string
  plan: string
}

interface ConnectedAppsData {
  status: 'linked' | 'unlinked' | 'upgrade_required'
  linked_family: LinkedFamily | null
  linkable_families: LinkedFamily[]
  is_apple_relay?: boolean
}

export function ConnectedApps() {
  const [data, setData] = useState<ConnectedAppsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)

  const fetchStatus = async () => {
    try {
      const response = await apiClient.get('/api/settings/connected-apps')
      setData(response.data)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchStatus() }, [])

  const handleLink = async (familyId: string) => {
    if (!confirm('Connect to this OurTurn family? Your custody schedule will sync automatically.')) return
    setActionLoading(true)
    try {
      await apiClient.post('/api/settings/connected-apps/link', { family_id: familyId })
      await fetchStatus()
    } catch {
      alert('Failed to connect. Please try again.')
    } finally {
      setActionLoading(false)
    }
  }

  const handleUnlink = async () => {
    if (!confirm('Disconnect from OurTurn? Schedule sync will stop but existing data is preserved.')) return
    setActionLoading(true)
    try {
      await apiClient.post('/api/settings/connected-apps/unlink')
      await fetchStatus()
    } catch {
      alert('Failed to disconnect. Please try again.')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-center opacity-50">Loading...</div>
  }

  if (!data) {
    return <div className="p-6 text-center opacity-50">Unable to load connection status.</div>
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Schedule Sync</h3>
        <p className="text-sm opacity-60">
          Connect with OurTurn to automatically sync your family schedule.
        </p>
      </div>

      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
            <ExternalLink className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <div className="font-medium">OurTurn</div>
            <div className="text-sm opacity-60">Custody scheduling</div>
          </div>
        </div>

        {data.status === 'linked' && data.linked_family && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-medium">Connected</span>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-medium">{data.linked_family.name}</div>
              <div className="text-xs opacity-60">{data.linked_family.slug} &middot; {data.linked_family.plan}</div>
              <div className="text-xs opacity-50 mt-1">Schedule syncs automatically</div>
            </div>
            <button
              onClick={handleUnlink}
              disabled={actionLoading}
              className="text-sm text-red-500 hover:text-red-600"
            >
              {actionLoading ? 'Disconnecting...' : 'Disconnect'}
            </button>
          </div>
        )}

        {data.status === 'unlinked' && (
          <div className="space-y-3">
            {data.linkable_families.length > 0 ? (
              <>
                <p className="text-sm opacity-70">
                  We found your OurTurn family. Connecting syncs your schedule automatically.
                </p>
                {data.linkable_families.map((family) => (
                  <div key={family.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <div>
                      <div className="font-medium">{family.name}</div>
                      <div className="text-xs opacity-60">{family.slug} &middot; {family.plan}</div>
                    </div>
                    <button
                      onClick={() => handleLink(family.id)}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {actionLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-sm opacity-60">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>No OurTurn family found for your email.</span>
                </div>
                {data.is_apple_relay && (
                  <p className="text-xs opacity-50">
                    Apple Sign In may use different emails per app. Try connecting from OurTurn Settings instead.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {data.status === 'upgrade_required' && (
          <div className="text-sm opacity-60 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>Upgrade your plan to connect with OurTurn.</span>
          </div>
        )}
      </div>
    </div>
  )
}
